import { Link, Outlet, createRootRoute } from '@tanstack/react-router';
import { useBillingStatus } from '../api/billing';
import { useSyncCloudCredentialsToHelper } from '../api/sync';
import { useAuth } from '../auth/AuthProvider';

export const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const { isAuthenticated, user, signIn, signOut, isLoading } = useAuth();
  const billing = useBillingStatus();
  // Push the user's access token to the paired helper so the helper's
  // outbound cloud-sync uploads have valid credentials. No-op if the
  // helper isn't paired in this browser.
  useSyncCloudCredentialsToHelper();
  const showTrialBanner =
    isAuthenticated &&
    billing.data &&
    !billing.data.has_perpetual_license &&
    billing.data.trial_days_remaining > 0 &&
    billing.data.trial_days_remaining <= 7;
  const showExpiredBanner =
    isAuthenticated &&
    billing.data &&
    !billing.data.has_perpetual_license &&
    billing.data.trial_days_remaining === 0 &&
    billing.data.trial_started_at !== null;
  return (
    <div style={{ minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 24px',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <nav style={{ display: 'flex', gap: 16 }}>
          <Link to="/" style={{ fontWeight: 600, textDecoration: 'none' }}>
            AI Assist
          </Link>
          <Link to="/topics">Topic Groups</Link>
          <Link to="/directories">Directories</Link>
          <Link to="/transcripts">Transcripts</Link>
          <Link to="/pair">Helper</Link>
          <Link to="/settings">Settings</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/onboarding">Onboarding</Link>
        </nav>
        <div>
          {isLoading ? (
            <span>…</span>
          ) : isAuthenticated ? (
            <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span>{user?.email ?? user?.name ?? 'Signed in'}</span>
              <button type="button" onClick={() => signOut()}>
                Sign out
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => signIn()}>
              Sign in
            </button>
          )}
        </div>
      </header>
      {showTrialBanner && billing.data && (
        <div
          style={{
            background: '#fef3c7',
            borderBottom: '1px solid #fcd34d',
            padding: '8px 24px',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            Your free trial ends in{' '}
            <strong>
              {billing.data.trial_days_remaining} day
              {billing.data.trial_days_remaining === 1 ? '' : 's'}
            </strong>
            .
          </span>
          <Link
            to="/pricing"
            style={{
              padding: '4px 12px',
              background: '#2563eb',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            View pricing
          </Link>
        </div>
      )}
      {showExpiredBanner && (
        <div
          style={{
            background: '#fee2e2',
            borderBottom: '1px solid #fca5a5',
            padding: '8px 24px',
            fontSize: 13,
            color: '#991b1b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            Your free trial has ended. Purchase the desktop license to keep using AI Assist.
          </span>
          <Link
            to="/pricing"
            style={{
              padding: '4px 12px',
              background: '#dc2626',
              color: 'white',
              textDecoration: 'none',
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            Buy license
          </Link>
        </div>
      )}
      <main style={{ padding: 24 }}>
        <Outlet />
      </main>
    </div>
  );
}
